import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Download, Star, ImageIcon, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { AssetImage } from '@/types/asset.types';
import { AssetImageManager } from './AssetImageManager';

interface AssetImageGalleryProps {
  assetId: string;
  images: AssetImage[];
  editable?: boolean;
  className?: string;
}

export function AssetImageGallery({
  assetId,
  images,
  editable = false,
  className,
}: AssetImageGalleryProps) {
  const { t } = useTranslation(['assets', 'common']);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev !== null && prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleDownload = async () => {
    if (!selectedImage) return;
    try {
      const response = await fetch(selectedImage.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedImage.name || 'asset-image.jpg';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Edit mode view
  if (editMode && editable) {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{t('images.manageImages')}</h4>
          <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
            {t('common:buttons.done')}
          </Button>
        </div>
        <AssetImageManager assetId={assetId} maxImages={5} />
      </div>
    );
  }

  // Empty state
  if (images.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 py-12', className)}>
        <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
        <p className="mt-3 text-sm text-muted-foreground">{t('images.noImages')}</p>
        {editable && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditMode(true)}
            className="mt-4"
          >
            <Pencil className="mr-2 h-4 w-4" />
            {t('images.addImages')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Image thumbnails - horizontal scrollable row */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {images.map((image, index) => (
          <button
            key={image.imageId}
            type="button"
            onClick={() => setSelectedIndex(index)}
            className="relative h-32 w-32 flex-shrink-0 overflow-hidden rounded-lg bg-muted ring-1 ring-border transition-all hover:ring-2 hover:ring-primary"
          >
            <img
              src={image.imageUrl}
              alt={image.name}
              className="h-full w-full object-cover"
            />
            {image.isPrimary && (
              <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
                {t('images.primary')}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Image count and edit button */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {images.length} {t('images.imagesCount')}
        </span>
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => setEditMode(true)}>
            <Pencil className="mr-2 h-3 w-3" />
            {t('images.edit')}
          </Button>
        )}
      </div>

      {/* Lightbox dialog */}
      <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
          <DialogTitle className="sr-only">{selectedImage?.name || 'Asset Image'}</DialogTitle>
          <div className="relative flex items-center justify-center bg-black">
            <img
              src={selectedImage?.imageUrl}
              alt={selectedImage?.name || 'Asset image'}
              className="max-h-[85vh] max-w-full object-contain"
            />

            {/* Navigation in lightbox */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}

            {/* Image info */}
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
              <span className="text-sm">
                {selectedIndex !== null ? selectedIndex + 1 : 0} / {images.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={handleDownload}
              >
                <Download className="mr-2 h-4 w-4" />
                {t('images.download')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
